import React, { Dispatch, SetStateAction, useState } from 'react';
import { Label } from '@/components/ui/label';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  isStarterNoCoverFeeWaiverEligible,
} from '../../../utils/dashboardFeeCalculation';
// @ts-expect-error - JS file without type declarations
import Card from '../../payment-methods/Card';
// @ts-expect-error - JS file without type declarations
import BankTransfer from '../../payment-methods/BankTransfer';
// @ts-expect-error - JS file without type declarations
import StripeCheckout from '../../StripeCheckout';
import { Elements } from '@stripe/react-stripe-js';
import { Stripe } from '@stripe/stripe-js';
import { CalendarDays } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import type { FinixForm, IframeState } from '../../drawers/types/FundraisingPaymentTypes';
import type { Merchant } from '../../../types/common';
import { TaxDeductibleControl } from './TaxDeductibleCard';

interface FeeCoverageRadioProps {
  checked: boolean;
  label: string;
  name: string;
  onChange: () => void;
  value: string;
}

function FeeCoverageRadio({
  checked,
  label,
  name,
  onChange,
  value,
}: FeeCoverageRadioProps) {
  return (
    <label className="inline-flex cursor-pointer items-center">
      <input
        type="radio"
        className="peer sr-only"
        name={name}
        value={value}
        checked={checked}
        onChange={onChange}
      />
      <span
        aria-hidden="true"
        className={cn(
          'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-edge bg-white transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-sage/30 peer-focus-visible:ring-offset-1',
          checked && 'border-sage bg-sage'
        )}
      >
        {checked && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
      </span>
      <span className="ml-2 text-sm text-ink-secondary">{label}</span>
    </label>
  );
}

// Helper function to get current time in the merchant's timezone (or local timezone as fallback)
const getCurrentTimeInTimezone = (merchant: Merchant | null): string => {
  const timezone = merchant?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  return new Date().toLocaleTimeString('en-US', {
    timeZone: timezone,
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Props interface for PaymentInformation component
// All state and handlers are passed from parent component (FundraiserLayout/EventLayout)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface PaymentInformationProps {
  presentation?: 'drawer' | 'workspace';

  // Form data - accepts any form data type that has payment fields
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formData: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setFormData: Dispatch<SetStateAction<any>>;

  // Merchant info
  merchant: Merchant | null;

  // Fee handling (online payments)
  feeCovered: boolean;
  handleFeeCoverageToggle: (covered: boolean) => void;

  // Validation
  invalidFields: Set<string>;
  handleFieldFocus: (fieldName: string) => void;

  // Payment method state
  paymentMethod: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setPaymentMethod: any;

  // Payment form refs and state
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  stripeCardRef: React.RefObject<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  finixCardRef: React.RefObject<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  finixBankRef: React.RefObject<any>;
  form: FinixForm | null;

  // Drawer state
  isDrawerOpen: boolean;

  // Edit mode
  isEditMode: boolean;

  // Payment type (passed from parent for controlled state)
  paymentType: string;

  // Stripe promise (initialized by parent)
  stripePromise: Promise<Stripe | null> | null;

  // Iframe states (managed by parent)
  iframeStates: IframeState | null;

  // Card submission function (managed by parent)
  updateWithCard: () => Promise<unknown>;

  // Bank account state (managed by parent)
  accountNumber: string;
  setAccountNumber: Dispatch<SetStateAction<string>>;
  routingNumber: string;
  setRoutingNumber: Dispatch<SetStateAction<string>>;
  accountType: 'CHECKING' | 'SAVINGS';
  setAccountType: Dispatch<SetStateAction<'CHECKING' | 'SAVINGS'>>;

  // Offline fee state (managed by parent)
  offlineFeeCovered: boolean;
  handleOfflineFeeCoverageToggle: (covered: boolean) => void;
  offlineCustomFeeAmount: string;
  handleOfflineCustomFeeAmountChange: (amount: string) => void;

  // Payment date/time state (managed by parent, for offline payments)
  paymentDate: Date;
  handlePaymentDateChange: (date: Date) => void;
  paymentTime: string;
  handlePaymentTimeChange: (time: string) => void;
  isDateToday: (date: Date | null) => boolean;
  isTimeInFuture: (time: string | null) => boolean;

  // Error handling state (managed by parent)
  setShowErrorAlert: Dispatch<SetStateAction<boolean>>;
  setSendingData: Dispatch<SetStateAction<boolean>>;
}

// Inline date picker for offline payment date selection
function PaymentDatePicker({
  paymentDate,
  handlePaymentDateChange,
}: {
  paymentDate: Date;
  handlePaymentDateChange: (date: Date) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);

  // Disable future dates — this picker is for recording PAST payment dates
  const isFutureDate = (date: Date) => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return date > today;
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={cn(
            'h-7 w-full justify-start rounded-md border-edge bg-white text-left text-sm font-normal',
          )}
        >
          <CalendarDays className="mr-2 h-4 w-4" />
          {paymentDate instanceof Date ? format(paymentDate, 'PPP') : <span>Pick a date</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0">
        <Calendar
          mode="single"
          selected={paymentDate instanceof Date ? paymentDate : undefined}
          onSelect={(date) => {
            if (date) {
              handlePaymentDateChange(date);
            }
            setIsOpen(false);
          }}
          disabled={isFutureDate}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

export default function PaymentInformation({
  presentation,
  formData,
  setFormData,
  merchant,
  feeCovered,
  handleFeeCoverageToggle,
  invalidFields,
  handleFieldFocus,
  paymentMethod,
  setPaymentMethod,
  stripeCardRef,
  finixCardRef,
  finixBankRef,
  form,
  isEditMode,
  paymentType,
  stripePromise,
  iframeStates,
  updateWithCard,
  accountNumber,
  setAccountNumber,
  routingNumber,
  setRoutingNumber,
  accountType,
  setAccountType,
  offlineFeeCovered,
  handleOfflineFeeCoverageToggle,
  offlineCustomFeeAmount,
  handleOfflineCustomFeeAmountChange,
  paymentDate,
  handlePaymentDateChange,
  paymentTime,
  handlePaymentTimeChange,
  isDateToday,
  isTimeInFuture,
  setShowErrorAlert,
  setSendingData,
}: PaymentInformationProps) {
  return (
    <div className="mt-2">
      {/* Tax Deductible Toggle */}
      {presentation !== 'workspace' && (
        <div className="mb-4 rounded-lg border border-edge bg-paper-muted px-4 py-3">
          <TaxDeductibleControl
            checked={formData.taxDeductible !== false}
            onCheckedChange={(taxDeductible) => {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              setFormData((prev: any) => ({ ...prev, taxDeductible }));
            }}
          />
        </div>
      )}

      {/* Payment Information Content */}
      <div className="mt-2">
        <div className="mt-2 space-y-3">
          {/* Payment Date - Only show for offline payments */}
          {paymentType !== 'Online' && (
            <div className="flex w-full gap-2">
              <div className="flex w-full flex-col">
                <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                  Payment Date
                </Label>
                <div className="mt-[0.3px]">
                  <PaymentDatePicker
                    paymentDate={paymentDate}
                    handlePaymentDateChange={handlePaymentDateChange}
                  />
                </div>
              </div>
              <div className="flex w-full flex-col">
                <label
                  htmlFor="payment-time"
                  className="block font-inter text-sm font-medium leading-5 text-ink-muted"
                >
                  Time
                </label>
                <div className="relative w-full">
                  <input
                    id="payment-time"
                    type="time"
                    value={paymentTime}
                    onChange={(e) => {
                      handlePaymentTimeChange(e.target.value);
                    }}
                    className={`block h-7 w-full rounded-lg border px-2 py-0 text-sm leading-tight text-black focus:outline-none focus:ring-0 ${
                      isDateToday(paymentDate) && isTimeInFuture(paymentTime)
                        ? 'border-red-500 focus:border-red-500'
                        : 'border-edge focus:border-edge'
                    }`}
                  />
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const currentTime = getCurrentTimeInTimezone(merchant);
                      handlePaymentTimeChange(currentTime);
                    }}
                    className="absolute right-8 top-1/2 -translate-y-1/2 p-1 text-sm text-ink-muted hover:text-ink-secondary"
                  >
                    Reset
                  </button>
                </div>
                {isDateToday(paymentDate) && isTimeInFuture(paymentTime) && (
                  <div className="mt-1 text-xs text-red-500">
                    Payment time cannot be in the future
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Online Payment Method Selection */}
          {!isEditMode && paymentType === 'Online' && (
            <div className="space-y-2">
              {/* Cover Transaction Fee */}
              <div>
                <div className="flex items-center gap-1">
                  <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                    Cover Transaction Fee
                  </Label>
                  {isStarterNoCoverFeeWaiverEligible(merchant) && (
                    <TooltipProvider delayDuration={0}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <InformationCircleIcon className="h-4 w-4 cursor-help text-ink-muted hover:text-ink-secondary" />
                        </TooltipTrigger>
                        <TooltipContent
                          side="top"
                          className="max-w-xs border border-edge bg-white text-black shadow-lg"
                        >
                          <p className="max-w-64 p-1 pl-3 pr-3 py-2 text-xs text-ink-muted">
                            For payments made on the dashboard, fees are not covered by the donor
                            and are absorbed by your organization. Non-dashboard payments still have
                            the fees absorbed by CharityStack.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
                <div className="grid grid-cols-2">
                  <FeeCoverageRadio
                    name="coverFee"
                    value="yes"
                    label="Yes"
                    checked={feeCovered}
                    onChange={() => handleFeeCoverageToggle(true)}
                  />
                  <FeeCoverageRadio
                    name="coverFee"
                    value="no"
                    label="No"
                    checked={!feeCovered}
                    onChange={() => handleFeeCoverageToggle(false)}
                  />
                </div>
              </div>

              {/* Payment Method Toggle */}
              <div className="mt-2">
                {(merchant?.defaultPaymentProcessor === 'FINIX' ||
                  !merchant?.defaultPaymentProcessor ||
                  merchant?.merchantIdentity) && (
                  <div className="h-auto">
                    {(merchant?.countryCode === 'US' || !merchant?.countryCode) && (
                      <div className="flex h-7 items-center justify-center gap-1 self-stretch rounded-md border border-edge bg-paper-muted p-1">
                        <button
                          type="button"
                          className={`flex-1 ${
                            paymentMethod === 'Card'
                              ? 'rounded-md border border-edge bg-paper-light'
                              : ''
                          } text-center font-inter text-sm font-medium leading-5 text-ink-muted`}
                          onClick={() => setPaymentMethod('Card')}
                        >
                          Card
                        </button>
                        <button
                          type="button"
                          className={`flex-1 ${
                            paymentMethod === 'Bank'
                              ? 'rounded-md border border-edge bg-paper-light'
                              : ''
                          } text-center font-inter text-sm font-medium leading-5 text-ink-muted`}
                          onClick={() => {
                            setPaymentMethod('Bank');
                          }}
                        >
                          Bank
                        </button>
                      </div>
                    )}

                    {/* Payment form containers */}
                    <div className="mt-2">
                      {/* Finix Card Form - Always render when form exists to allow iframe preloading */}
                      {(merchant?.defaultPaymentProcessor === 'FINIX' ||
                        !merchant?.defaultPaymentProcessor) &&
                        form !== null && (
                          <div className={paymentMethod !== 'Card' ? 'hidden' : ''}>
                            <Card
                              iframeStates={iframeStates}
                              finixCardRef={finixCardRef}
                              updateWithCard={updateWithCard}
                              form={form}
                              backgroundColor={presentation === 'workspace' ? '#FFFFFF' : undefined}
                              key="card-form"
                              options={{
                                style: {
                                  base: {
                                    fontSize: '16px',
                                    color: '#424770',
                                    '::placeholder': {
                                      color: '#aab7c4',
                                    },
                                  },
                                  invalid: {
                                    color: '#9e2146',
                                  },
                                },
                              }}
                            />
                          </div>
                        )}

                      {/* Bank Transfer Form for Finix */}
                      {(merchant?.defaultPaymentProcessor === 'FINIX' ||
                        !merchant?.defaultPaymentProcessor ||
                        merchant?.merchantIdentity) &&
                        (merchant?.countryCode === 'US' || !merchant?.countryCode) &&
                        paymentMethod === 'Bank' && (
                          <BankTransfer
                            finixBankRef={finixBankRef}
                            accountNumber={accountNumber}
                            setAccountNumber={setAccountNumber}
                            routingNumber={routingNumber}
                            setRoutingNumber={setRoutingNumber}
                            accountType={accountType}
                            setAccountType={setAccountType}
                          />
                        )}
                    </div>
                  </div>
                )}

                {/* Stripe Card Form */}
                {merchant?.defaultPaymentProcessor === 'STRIPE' &&
                  stripePromise &&
                  paymentMethod === 'Card' && (
                    <div className="mt-2">
                      <Elements stripe={stripePromise}>
                        <StripeCheckout
                          ref={stripeCardRef}
                          setErrorMessage={() => {}}
                          setShowErrorAlert={setShowErrorAlert}
                          setSendingData={setSendingData}
                          defaultPaymentProcessor={'STRIPE'}
                          stripeAccountID={merchant.stripeAccountID}
                        />
                      </Elements>
                    </div>
                  )}
              </div>
            </div>
          )}

          {/* Offline Payment Custom Fee Section */}
          {paymentType !== 'Online' && (
            <div className="space-y-3">
              {/* Cover Transaction Fee */}
              <div>
                <div className="flex items-center gap-1">
                  <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                    Cover Transaction Fee
                  </Label>
                  {isStarterNoCoverFeeWaiverEligible(merchant) && (
                    <TooltipProvider delayDuration={0}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <InformationCircleIcon className="h-4 w-4 cursor-help text-ink-muted hover:text-ink-secondary" />
                        </TooltipTrigger>
                        <TooltipContent
                          side="top"
                          className="max-w-xs border border-edge bg-white text-black shadow-lg"
                        >
                          <p className="max-w-64 p-1 pl-3 pr-3 py-2 text-xs text-ink-muted">
                            For payments made on the dashboard, fees are not covered by the donor
                            and are absorbed by your organization. Non-dashboard payments still have
                            the fees absorbed by CharityStack.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
                <div className="grid grid-cols-2">
                  <FeeCoverageRadio
                    name="offlineCoverFee"
                    value="yes"
                    label="Yes"
                    checked={offlineFeeCovered}
                    onChange={() => handleOfflineFeeCoverageToggle(true)}
                  />
                  <FeeCoverageRadio
                    name="offlineCoverFee"
                    value="no"
                    label="No"
                    checked={!offlineFeeCovered}
                    onChange={() => handleOfflineFeeCoverageToggle(false)}
                  />
                </div>
              </div>

              {/* Custom Fee Amount - Only show when "Yes" is selected */}
              {offlineFeeCovered && (
                <div className="space-y-3 rounded-md border border-edge bg-paper-muted p-3">
                  <div>
                    <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                      Custom Fee Amount
                    </Label>
                    <div className="relative">
                      <span className="absolute left-[10px] top-1/2 -translate-y-1/2 text-sm leading-none text-ink-muted">
                        $
                      </span>
                      <input
                        id="offline_custom_fee"
                        name="offline_custom_fee"
                        type="text"
                        value={offlineCustomFeeAmount}
                        onChange={(e) => handleOfflineCustomFeeAmountChange(e.target.value)}
                        onFocus={() => handleFieldFocus('offline_custom_fee')}
                        className={`block h-7 w-full rounded-md pl-5 pr-12 text-sm leading-none focus:border-edge focus:outline-none focus:ring-0 ${
                          invalidFields.has('offline_custom_fee')
                            ? 'border-red-500'
                            : 'border-edge bg-white'
                        }`}
                        placeholder="0.00"
                      />
                      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-ink-muted">
                        USD
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
